import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="notifications-center"
export default class extends Controller {
  static targets = ["dropdown"]
  static values = { userId: String }

  connect() {
    // Close dropdown when clicking outside
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this.boundCloseOnOutsideClick)
    
    // Connect to ActionCable for real-time notifications
   // this.connectToActionCable()
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
    
    // Disconnect from ActionCable
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  toggle(event) {
    event.stopPropagation()
    
    if (this.dropdownTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.dropdownTarget.classList.remove("hidden")
    this.dropdownTarget.setAttribute("aria-hidden", "false")
    
    // Focus management for accessibility
    setTimeout(() => {
      const firstFocusable = this.dropdownTarget.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      if (firstFocusable) {
        firstFocusable.focus()
      }
    }, 100)
  }

  close() {
    this.dropdownTarget.classList.add("hidden")
    this.dropdownTarget.setAttribute("aria-hidden", "true")
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  connectToActionCable() {
    if (!this.userIdValue) return
    
    // Import ActionCable consumer
    import("@rails/actioncable").then(({ createConsumer }) => {
      const consumer = createConsumer()
      
      this.subscription = consumer.subscriptions.create(
        {
          channel: "Noticed::NotificationChannel"
        },
        {
          received: (data) => {
            this.handleNotificationReceived(data)
          },
          
          connected: () => {
            console.log("Connected to notifications channel")
          },
          
          disconnected: () => {
            console.log("Disconnected from notifications channel")
          }
        }
      )
    })
  }

  handleNotificationReceived(data) {
    console.log("Received notification via ActionCable:", data)
    
    // Handle Turbo Stream updates
    if (data.type === "turbo_stream" && data.turbo_stream) {
      // Apply Turbo Stream actions directly
      Turbo.renderStreamMessage(data.turbo_stream)
      
      // Add visual feedback animations
      this.addNotificationAnimations()
    }
  }

  addNotificationAnimations() {
    // Add a noticeable animation when notifications update
    setTimeout(() => {
      const badge = this.element.querySelector('.notification-badge')
      const bellIcon = this.element.querySelector('button[data-action*="toggle"]')
      
      if (badge) {
        // Animate the badge with a bounce effect
        badge.classList.add('animate-bounce')
        setTimeout(() => {
          badge.classList.remove('animate-bounce')
          badge.classList.add('animate-pulse')
          setTimeout(() => {
            badge.classList.remove('animate-pulse')
          }, 2000)
        }, 1000)
      }
      
      if (bellIcon) {
        // Add a subtle pulse to the bell
        bellIcon.classList.add('animate-pulse')
        setTimeout(() => {
          bellIcon.classList.remove('animate-pulse')
        }, 1000)
      }
    }, 200)
  }

  // Removed refreshNotificationCenter() - using Turbo Streams instead

  markAsRead(event) {
    const notificationId = event.currentTarget.dataset.notificationId
    const notificationElement = this.element.querySelector(`[data-notification-id="${notificationId}"]`)
    
    if (!notificationElement) return

    // Make API call to mark as read
    fetch(`/notifications/${notificationId}/mark_as_read`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    })
    .then(response => {
      if (response.ok) {
        // Update UI immediately
        this.updateNotificationAsRead(notificationElement)
        this.updateUnreadCount()
      } else {
        console.error('Failed to mark notification as read')
      }
    })
    .catch(error => {
      console.error('Error marking notification as read:', error)
    })
  }

  markAllRead(event) {
    event.preventDefault()
    
    // Make API call to mark all as read
    fetch('/notifications/mark_all_as_read', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    })
    .then(response => {
      if (response.ok) {
        // Update UI immediately
        this.markAllNotificationsAsRead()
        this.updateUnreadCount()
      } else {
        console.error('Failed to mark all notifications as read')
      }
    })
    .catch(error => {
      console.error('Error marking all notifications as read:', error)
    })
  }

  dismiss(event) {
    const notificationId = event.currentTarget.dataset.notificationId
    const notificationElement = this.element.querySelector(`[data-notification-id="${notificationId}"]`)
    
    if (!notificationElement) return

    // Make API call to dismiss/delete notification
    fetch(`/notifications/${notificationId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    })
    .then(response => {
      if (response.ok) {
        // Remove from UI with animation
        notificationElement.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
        notificationElement.style.opacity = '0'
        notificationElement.style.transform = 'translateX(100%)'
        
        setTimeout(() => {
          notificationElement.remove()
          this.updateUnreadCount()
          
          // Check if we need to show empty state
          const remainingNotifications = this.element.querySelectorAll('.notification-item')
          if (remainingNotifications.length === 0) {
            this.showEmptyState()
          }
        }, 300)
      } else {
        console.error('Failed to dismiss notification')
      }
    })
    .catch(error => {
      console.error('Error dismissing notification:', error)
    })
  }

  // Private methods for UI updates
  updateNotificationAsRead(notificationElement) {
    // Remove unread styling
    notificationElement.classList.remove('bg-blue-50', 'dark:bg-blue-900/20', 'notification-unread')
    notificationElement.dataset.notificationUnread = 'false'
    
    // Remove unread indicator
    const unreadIndicator = notificationElement.querySelector('.bg-theme-accent')
    if (unreadIndicator && unreadIndicator.classList.contains('w-0.5')) {
      unreadIndicator.remove()
    }
    
    // Remove mark as read button
    const markAsReadBtn = notificationElement.querySelector('[data-action*="markAsRead"]')
    if (markAsReadBtn) {
      markAsReadBtn.remove()
    }
  }

  markAllNotificationsAsRead() {
    const unreadNotifications = this.element.querySelectorAll('.notification-unread')
    unreadNotifications.forEach(notification => {
      this.updateNotificationAsRead(notification)
    })
    
    // Hide "Mark all read" button
    const markAllReadBtn = this.element.querySelector('[data-action*="markAllRead"]')
    if (markAllReadBtn) {
      markAllReadBtn.style.display = 'none'
    }
  }

  updateUnreadCount() {
    const unreadNotifications = this.element.querySelectorAll('[data-notification-unread="true"]')
    const count = unreadNotifications.length
    
    // Update badge
    const badge = this.element.querySelector('.notification-badge')
    if (count > 0) {
      if (badge) {
        badge.textContent = count > 99 ? '99+' : count.toString()
      }
    } else {
      if (badge) {
        badge.remove()
      }
    }
    
    // Update header text
    const headerText = this.element.querySelector('.text-xs.text-gray-500')
    if (headerText) {
      if (count > 0) {
        headerText.textContent = count === 1 ? '1 unread message' : `${count} unread messages`
      } else {
        headerText.remove()
      }
    }
  }

  showEmptyState() {
    const listContainer = this.element.querySelector('.notification-dropdown')
    if (listContainer) {
      listContainer.innerHTML = `
        <div class="px-6 py-12 text-center notification-empty-state">
          <div class="flex flex-col items-center space-y-4">
            <div class="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-theme-background-alt">
              <svg class="h-8 w-8 text-theme-muted dark:text-theme-text-light" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
              </svg>
            </div>
            <div class="space-y-2">
              <h4 class="text-sm font-medium text-gray-900 dark:text-theme-primary">No notifications yet</h4>
              <p class="text-xs text-gray-500 dark:text-theme-text-muted max-w-xs">
                You'll see campaign updates, system alerts, and other important information here.
              </p>
            </div>
          </div>
        </div>
      `
    }
  }
} 